import axiosClient from '../clients/axiosClient';

export interface AudioAttachment {
  url: string;
  duration: number;
  key?: string;
}

export interface CreateNoteRequest {
  member_id: string;
  description: string;
  title: string;
  is_private: boolean;
  mentioned_user_ids: string[];
  category?: string;
  note_template_id?: number;
  is_referral_letter?: boolean;
  audio_urls?: string[];
  audio_attachments?: Array<{ key: string; duration: number }>;
}

export interface NoteResponse {
  id: number;
  description: string;
  member_id: string;
  author: {
    id: string;
    first_name: string;
    last_name: string;
    role: string;
  };
  created_at: string;
  updated_at: string;
  title: string;
  is_private: boolean;
  category?: string;
  note_template_id?: number;
  is_referral_letter?: boolean;
  audio_urls?: string[];
  audio_attachments?: AudioAttachment[];
  attachments?: AudioAttachment[];
}

export interface DeleteNoteRequest {
  note_id: number;
}

export interface EditNoteRequest {
  note_id: number;
  description?: string;
  title?: string;
  is_private?: boolean;
  category?: string;
  note_template_id?: number;
  is_referral_letter?: boolean;
  mentioned_user_ids?: string[];
  audio_urls?: string[];
  audio_attachments?: Array<{ key: string; duration: number }>;
}

export interface GetNotesListResponse {
  notes: NoteResponse[];
  total_count: number;
}

export interface MarkAllReadResponse {
  success: boolean;
  message: string;
}

// API Functions
export const createNote = async (noteData: CreateNoteRequest): Promise<NoteResponse> => {
  const response = await axiosClient.post('/api/v1/notes/', noteData);
  return response.data;
};

export const deleteNote = async (noteData: DeleteNoteRequest): Promise<void> => {
  await axiosClient.delete('/api/v1/notes/', { data: noteData });
};

export const editNote = async (noteData: EditNoteRequest): Promise<{ status: string }> => {
  const response = await axiosClient.patch('/api/v1/notes/', noteData);
  return response.data;
};

export const getMemberNotes = async (
  memberId: string,
  offset: number = 0,
  limit: number = 10,
  sortField?: 'created_at' | 'updated_at',
  sortOrder?: 'asc' | 'desc',
  isReferralLetter?: boolean
): Promise<GetNotesListResponse> => {
  const params = new URLSearchParams({
    member_id: memberId,
    offset: offset.toString(),
    limit: limit.toString(),
  });

  if (sortField) {
    params.append('sort_field', sortField);
  }
  if (sortOrder) {
    params.append('sort_order', sortOrder);
  }
  if (isReferralLetter !== undefined) {
    params.append('is_referral_letter', String(isReferralLetter));
  }

  const response = await axiosClient.get(`/api/v1/notes/list?${params.toString()}`);
  return response.data;
};

export const getNoteById = async (noteId: number): Promise<NoteResponse> => {
  const response = await axiosClient.get(`/api/v1/notes/${noteId}`);
  return response.data;
};

export const markAllNotesRead = async (): Promise<MarkAllReadResponse> => {
  const response = await axiosClient.post('/api/v1/notes/mark-all-read');
  return response.data;
};
